//src/hooks/useApiLock.ts
import { useState, useCallback } from 'react';

/**
 * Hook para gerenciar locks em chamadas API, prevenindo envios duplicados.
 * 
 * @description
 * Cria um sistema de bloqueio reutilizável por chave única (ex.: `${endpoint}_${itemId}`).
 * Útil para evitar race conditions em ações assíncronas como drag-and-drop ou cliques rápidos.
 * 
 * @returns {Object} Objeto com métodos para verificar, bloquear e desbloquear chaves.
 * - `isLocked(key: string): boolean` - Verifica se a chave está bloqueada.
 * - `lock(key: string): void` - Bloqueia a chave.
 * - `unlock(key: string): void` - Desbloqueia a chave.
 * 
 * @example
 * const { isLocked, lock, unlock } = useApiLock();
 * const key = `/api/endpoint/${id}`;
 * if (isLocked(key)) return;
 * lock(key);
 * try {
 *   // Lógica assíncrona
 * } finally {
 *   unlock(key);
 * }
 */
export const useApiLock = () => {
  const [locks, setLocks] = useState<Set<string>>(new Set());

  const isLocked = useCallback((key: string) => locks.has(key), [locks]);

  const lock = useCallback((key: string) => {
    setLocks((prev) => new Set([...prev, key]));
  }, []);

  const unlock = useCallback((key: string) => {
    setLocks((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  return { isLocked, lock, unlock };
};